import type { z } from "zod";

import V from "@/common/helpers/validate";

/**
 * A generic "required" refinement function. It adds a check
 * that the value must have a `length` > 0.
 *
 * By default, it uses the message "Cannot be empty", but you can
 * provide a custom message as a second argument.
 */
export function required<T extends z.ZodTypeAny>(schema: T, message = "Field is required"): z.ZodEffects<T> {
  return schema.refine((value) => V.required(value), {
    params: {
      i18n: {
        key: "validation.required_field",
        values: {},
      },
    },
    message,
  });
}

/**
 * A generic "hex colour" refinement function. It checks the value is a
 * valid hex colour code (e.g. #FF0000).
 */
export function hex<T extends z.ZodTypeAny>(schema: T): z.ZodEffects<T> {
  return schema.refine(
    (value) => {
      const re = /^#[a-fA-F0-9]{6}$/;

      return re.test(String(value));
    },
    {
      params: {
        i18n: {
          key: "validation.color_invalid",
          values: {},
        },
      },
    }
  );
}

/**
 * A schema for validating jersey numbers.
 * Accepts:
 * - Single digit: 0-9
 * - Double zero: 00
 * - Double digits: 00-099
 * - Triple digits: 100-999
 */
export function jerseyNumber<T extends z.ZodTypeAny>(schema: T): z.ZodEffects<z.ZodEffects<T>> {
  return schema
    .refine(
      (value) => {
        return /^\d+$/.test(String(value));
      },
      {
        params: {
          i18n: {
            key: "errors.jersey_not_number",
            values: {},
          },
        },
      }
    )
    .refine(
      (value) => {
        const re = /^(?:0|00|0[1-9]\d?|[1-9]\d{0,2})$/;
        return re.test(String(value));
      },
      {
        params: {
          i18n: {
            key: "errors.jersey_not_in_range",
            values: {
              min: "0",
              max: "999",
              leading_min: "01",
              leading_max: "099",
            },
          },
        },
      }
    );
}
